import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * 主题模式。
 *
 * - `system`：跟随操作系统（默认）；
 * - `light` / `dark`：用户显式选择，写入 localStorage 后长期生效。
 *
 * 与 `@en3/shared` 的 `UserSettings.theme` 保持一致，将来登录后可以把本地选择
 * 同步到用户设置里（跨设备一致）。
 */
export type ThemeMode = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'en3-theme';

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** 把模式解析成实际生效的明暗。 */
export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return mode;
}

/** 把主题写到 <html>：Tailwind 的 dark 变体与浏览器原生控件（表单/滚动条）都依赖它。 */
export function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(mode);
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  document.documentElement.style.colorScheme = resolved;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'system',
      setMode: (mode) => {
        set({ mode });
        applyTheme(mode);
      },
      toggle: () => {
        // 在浅/深之间切换；从 system 出发时切到与当前相反的那一侧
        const current = resolveTheme(get().mode);
        const next: ThemeMode = current === 'dark' ? 'light' : 'dark';
        set({ mode: next });
        applyTheme(next);
      },
    }),
    {
      name: THEME_STORAGE_KEY,
      // 只持久化选择，不持久化函数
      partialize: (state) => ({ mode: state.mode }),
      onRehydrateStorage: () => (state) => {
        applyTheme(state?.mode ?? 'system');
      },
    },
  ),
);

/**
 * 跟随系统主题：仅在 `mode === 'system'` 时响应系统切换。
 * 在应用启动时调用一次即可（返回取消订阅函数）。
 */
export function watchSystemTheme(): () => void {
  if (typeof window === 'undefined') return () => {};
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    if (useThemeStore.getState().mode === 'system') applyTheme('system');
  };
  media.addEventListener('change', handler);
  return () => media.removeEventListener('change', handler);
}
